import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Mail, MailCheck, RotateCcw, ShieldCheck } from "lucide-react";

import {
  AuthAlert,
  AuthField,
  AuthHeading,
  AuthPrimaryButton,
} from "./auth-primitives";
import { authFormWidthClass } from "./auth-tokens";
import {
  DEFAULT_FORGOT_PASSWORD_COPY,
  type ForgotPasswordFormCopy,
} from "./auth-copy";

export interface ForgotPasswordFormProps {
  onSubmit: (email: string) => Promise<void>;
  onNavigateToSignin: () => void;
  variant?: "default" | "admin";
  logoSrc?: string;
  /** Translated labels, merged over the English defaults. See `auth-copy.ts`. */
  copy?: Partial<ForgotPasswordFormCopy>;
}

export const ForgotPasswordForm = ({
  onSubmit,
  onNavigateToSignin,
  variant = "default",
  logoSrc,
  copy,
}: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = variant === "admin";
  const c = { ...DEFAULT_FORGOT_PASSWORD_COPY, ...copy };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit(email);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : c.sendFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAnotherEmail = () => {
    setSuccess(false);
    setEmail("");
    setError(null);
  };

  return (
    <div className={authFormWidthClass}>
      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="sent"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
          >
            <div className="relative mx-auto mb-5 h-20 w-20">
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
              <div className="relative flex h-full w-full items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
                <MailCheck className="h-9 w-9 text-primary" />
              </div>
              <motion.span
                initial={{ scale: 0.8, opacity: 0.6 }}
                animate={{ scale: 1.6, opacity: 0 }}
                transition={{ duration: 1.6, ease: "easeOut", repeat: Infinity, repeatDelay: 1.2 }}
                className="absolute inset-0 rounded-full border border-primary/30"
              />
            </div>

            <h1 className="font-display text-[26px] font-semibold tracking-tight text-stone-900 dark:text-stone-50">
              {c.sentTitle}
            </h1>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-stone-500 dark:text-stone-400">
              {c.sentDescription(email)}
            </p>

            <div className="mt-7 space-y-2.5">
              <AuthPrimaryButton type="button" onClick={onNavigateToSignin}>
                <ArrowLeft className="h-4 w-4" />
                {c.backToSignIn}
              </AuthPrimaryButton>
              <button
                type="button"
                onClick={handleTryAnotherEmail}
                className="flex w-full items-center justify-center gap-1.5 text-[13px] font-medium text-stone-500 transition-colors hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-100"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {c.tryAnotherEmail}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <AuthHeading
              icon={
                isAdmin ? (
                  <ShieldCheck className="h-6 w-6 text-primary" />
                ) : (
                  <img src={logoSrc || "/icon.png"} alt="" className="h-9 w-9 object-contain" />
                )
              }
              title={c.title}
              description={c.description}
            />

            <form onSubmit={handleForgotPassword} className="mt-7 space-y-4">
              <AuthField
                name="email"
                label={c.emailLabel}
                icon={<Mail className="h-[18px] w-[18px]" />}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder={isAdmin ? "admin@example.com" : "you@example.com"}
                autoComplete="email"
                required
              />

              <AuthAlert message={error} />

              <div className="pt-1">
                <AuthPrimaryButton loading={loading} loadingLabel={c.sending}>
                  {c.submit}
                  <ArrowRight className="h-4 w-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5" />
                </AuthPrimaryButton>
              </div>
            </form>

            <button
              type="button"
              onClick={() => {
                setEmail("");
                onNavigateToSignin();
              }}
              className="mx-auto mt-6 flex items-center gap-1.5 text-[13px] font-medium text-stone-500 transition-colors hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-100"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {c.backToSignIn}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ForgotPasswordForm;